import { Printer } from '../components/labels/PrinterManager';
import { LabelTemplate, LabelElement } from '../components/labels/LabelDesigner';
import { Product } from '../firebase/services/productService';
import jsPDF from 'jspdf';
import { addDoc, collection, serverTimestamp, setDoc, doc, query, where, getDocs, getDoc } from 'firebase/firestore';
import { db } from '../firebase/config';
import QRCode from 'qrcode';
import { getEnv } from '../utils/browserEnv';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { useAuth } from '../contexts/AuthContext';

// Define the PrintAgent type
interface PrintAgent {
  id: string;
  name: string;
  status: string;
  lastSeen: string;
  hostname: string;
  port: number;
  printers: Array<{
    id: string;
    name: string;
    type: string;
    address: string;
    port: number;
    manufacturer: string;
    model: string;
    dpi: number;
    status: string;
  }>;
}

class PrintService {
  // Base URL for Firebase functions
  private functionsBaseUrl = 'https://us-central1-fllboats.cloudfunctions.net';
  
  /**
   * Get available Print Agents
   */
  async getAvailablePrintAgents(): Promise<PrintAgent[]> {
    try {
      if (process.env.NODE_ENV === 'development') {
        console.log('Fetching available Print Agents...');
      }
      
      // Get current user ID from localStorage or sessionStorage
      const userId = localStorage.getItem('userId') || sessionStorage.getItem('userId');
      if (!userId) {
        if (process.env.NODE_ENV === 'development') {
          console.warn('No authenticated user found in storage');
        }
        return [];
      }
      
      // Get user role from localStorage or sessionStorage
      const userRole = localStorage.getItem('userRole') || sessionStorage.getItem('userRole');
      if (!userRole) {
        if (process.env.NODE_ENV === 'development') {
          console.warn('No user role found in storage');
        }
        return [];
      }
      
      if (process.env.NODE_ENV === 'development') {
        console.log(`Found authenticated user: ${userId} with role: ${userRole}`);
      }
      
      // Base URL for Firebase functions
      const functionsBaseUrl = 'https://us-central1-fllboats.cloudfunctions.net';
      
      // Send request to Cloud Functions
      const response = await fetch(
        `${functionsBaseUrl}/listPrintAgents?userId=${encodeURIComponent(userId)}&userRole=${encodeURIComponent(userRole)}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          }
        }
      );
      
      if (!response.ok) {
        const errorData = await response.json();
        if (process.env.NODE_ENV === 'development') {
          console.error('Failed to fetch print agents:', errorData);
        }
        return [];
      }
      
      const data = await response.json();
      if (process.env.NODE_ENV === 'development') {
        console.log('Print agents fetched successfully:', data);
      }
      
      if (!data.success || !Array.isArray(data.agents)) {
        if (process.env.NODE_ENV === 'development') {
          console.error('Invalid response format from Cloud Functions');
        }
        return [];
      }
      
      return data.agents;
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Error fetching print agents:', error);
      }
      return [];
    }
  }
  
  /**
   * Queue a print job to a specific Print Agent
   */
  async queuePrintJob(agentId: string, printerId: string, printData: string, format = 'zpl'): Promise<boolean> {
    try {
      if (process.env.NODE_ENV === 'development') {
        console.log(`Queueing print job to Cloud Functions for agent: ${agentId}, printer: ${printerId}, format: ${format}`);
      }
      
      // Call the API to queue the print job
      const response = await fetch(`${this.functionsBaseUrl}/queuePrintJob`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          agentId,
          printerId,
          printData,
          format
        }),
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        if (process.env.NODE_ENV === 'development') {
          console.error('Failed to queue print job:', errorData);
        }
        throw new Error(`Failed to queue print job: ${errorData.message || response.statusText}`);
      }
      
      const data = await response.json();
      
      if (!data.success) {
        if (process.env.NODE_ENV === 'development') {
          console.error('Print job queue error:', data);
        }
        throw new Error(data.message || 'Unknown error');
      }
      
      if (process.env.NODE_ENV === 'development') {
        console.log(`Print job queued successfully to Cloud Functions: ${data.jobId}`);
      }
      return true;
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Error queueing print job to Cloud Functions:', error);
      }
      return false;
    }
  }

  /**
   * Queue a print job to a specific Print Agent using the new outbound agent
   * This method is optimized for the outbound-only print agent model
   */
  async queuePrintJobOutbound(
    agentId: string,
    printer: string,
    printData: string,
    options: any = {},
    documentId: string | null = null,
    userId: string | null = null
  ): Promise<{ success: boolean; jobId?: string; message: string }> {
    try {
      if (process.env.NODE_ENV === 'development') {
        console.log(`Queueing print job to outbound agent: ${agentId}, printer: ${printer}`);
      }
      
      // Call the API to queue the print job
      const response = await fetch(`${this.functionsBaseUrl}/queuePrintJob`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          agentId,
          printer,
          printData,
          options,
          documentId,
          userId
        }),
      });
      
      if (!response.ok) {
        let errorData;
        try {
          errorData = await response.json();
        } catch (e) {
          errorData = { error: response.statusText };
        }
        if (process.env.NODE_ENV === 'development') {
          console.error('Failed to queue print job:', errorData);
        }
        throw new Error(`Failed to queue print job: ${errorData.error || response.statusText}`);
      }
      
      const data = await response.json();
      
      if (!data.success) {
        if (process.env.NODE_ENV === 'development') {
          console.error('Print job queue error:', data);
        }
        throw new Error(data.error || 'Unknown error');
      }
      
      if (process.env.NODE_ENV === 'development') {
        console.log(`Print job queued successfully to outbound agent: ${data.jobId}`);
      }
      return {
        success: true,
        jobId: data.jobId,
        message: data.message || 'Print job queued successfully'
      };
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Error queueing print job to outbound agent:', error);
      }
      return {
        success: false,
        message: error instanceof Error ? error.message : String(error)
      };
    }
  }

  /**
   * Main print method that handles routing to appropriate printer type
   * Updated to prioritize Firebase Cloud Functions over direct connections
   */
  async printLabel(
    printer: Printer,
    template: LabelTemplate,
    products: Product[],
    copies: number = 1
  ): Promise<{ success: boolean; message: string }> {
    try {
      // Check if printer was provided
      if (!printer) {
        return {
          success: false,
          message: "No printer specified. Please configure a printer first."
        };
      }
      
      // Detect environment
      const isDev = window.location.hostname === 'localhost' || 
                   window.location.hostname === '127.0.0.1';
      
      if (process.env.NODE_ENV === 'development') {
        console.log(`Starting print job for printer: ${printer.name} in ${isDev ? 'DEVELOPMENT' : 'PRODUCTION'} mode`);
      }
      
      // Generate the label content (ZPL data, PDF, etc.)
      const isZebraPrinter = printer.manufacturer?.toLowerCase().includes('zebra');
      let printData;
      const format: 'zpl' | 'esc-pos' | 'pdf' = isZebraPrinter ? 'zpl' : 'pdf';
      
      if (isZebraPrinter) {
        // Generate ZPL code
        printData = this.generateZPL(printer, template, products, copies);
      } else {
        // Create a PDF blob
        const pdfBlob = await this.generatePDF(printer, template, products, copies);
        // Convert it to base64
        printData = await this.blobToBase64(pdfBlob);
      }
      
      // For development environment, try direct connection to the print agent first
      if (isDev) {
        if (process.env.NODE_ENV === 'development') {
          console.log('Development mode: attempting direct connection to Print Agent');
        }
        try {
          // Try direct printing first in dev mode
          const agentConfig = this.getPrintAgentConfig();
          
          if (!agentConfig.enabled) {
            throw new Error('Print agent is not enabled');
          }
          
          // Check if the agent is available
          const agentAvailable = await this.checkLocalPrintAgent(agentConfig.hostname, agentConfig.port);
          
          if (agentAvailable) {
            if (process.env.NODE_ENV === 'development') {
              console.log('Print Agent is available locally, sending print job directly');
            }
            const result = await this.printViaLocalAgent(printer, printData, format);
            if (result.success) {
              return result;
            } else {
              if (process.env.NODE_ENV === 'development') {
                console.warn('Direct printing failed, falling back to cloud functions:', result.message);
              }
            }
          } else {
            if (process.env.NODE_ENV === 'development') {
              console.log('Print Agent not available locally, using cloud functions');
            }
          }
        } catch (error) {
          if (process.env.NODE_ENV === 'development') {
            console.warn('Error attempting direct connection to Print Agent:', error);
          }
        }
      }
      
      // In production (or as fallback), use Firebase Cloud Functions
      if (process.env.NODE_ENV === 'development') {
        console.log('Using Firebase Cloud Functions for printing');
      }
      
      // Try to use the existing "agentId" field on the printer
      // If it exists, use it, otherwise try to find an available agent
      try {
        let agentId = printer.agentId;
        const printerId = printer.name; // Use the printer name for outbound agents
        
        // If we don't have an agent ID, try to get the first available agent
        if (!agentId) {
          const agents = await this.getAvailablePrintAgents();
          if (agents && agents.length > 0) {
            // Find online agents first
            const onlineAgent = agents.find(a => a.status === 'ONLINE');
            agentId = onlineAgent ? onlineAgent.id : agents[0].id;
            if (process.env.NODE_ENV === 'development') {
              console.log(`Using available agent: ${agentId}`);
            }
          } else {
            if (process.env.NODE_ENV === 'development') {
              console.warn('No Print Agents available');
            }
            
            // In development, we can store the print job locally
            if (isDev) {
              const localStorageResult = await this.storePrintDataInLocalStorage(
                printData, 
                format,
                printer.id
              );
              return localStorageResult;
            } else {
              return {
                success: false,
                message: "No Print Agents available. Please make sure a Print Agent is running."
              };
            }
          }
        }
        
        // Queue the print job with the outbound agent method
        const result = await this.queuePrintJobOutbound(
          agentId,
          printerId,
          printData,
          {
            format,
            copies: copies || 1
          },
          null, // documentId could be included in the future
          null  // userId could be included in the future
        );
        
        if (result.success) {
          return {
            success: true,
            message: result.message || "Print job queued successfully"
          };
        } else {
          throw new Error(result.message || "Failed to queue print job");
        }
      } catch (cloudError) {
        if (process.env.NODE_ENV === 'development') {
          console.error('Cloud function error:', cloudError);
        }
        
        // In development, we can store the print job locally as a fallback
        if (isDev) {
          const localStorageResult = await this.storePrintDataInLocalStorage(
            printData, 
            format,
            printer.id
          );
          return localStorageResult;
        }
        
        // Generate a QR code with print data for manual scanning
        const qrCodeResult = await this.showPrintQrCode(
          printData, 
          format
        );
        return qrCodeResult;
      }
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Error in printLabel:', error);
      }
      return {
        success: false,
        message: error instanceof Error ? error.message : String(error)
      };
    }
  }

  /**
   * Try to print directly to a Zebra printer on the local network
   */
  private async tryDirectZebraPrinting(
    printer: Printer,
    zplData: string
  ): Promise<{ success: boolean; message: string }> {
    try {
      // Attempt direct connection to the printer's built-in web server
      // Many Zebra printers support HTTP POST to send ZPL
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 3000); // 3 second timeout
      
      // Try different common Zebra printer endpoints
      const endpoints = [
        '/printer/print',
        '/pstprnt',
        '/zpl',
        '/zebra/print',
        '/write',           // Sometimes used by newer models
        '/api/print',       // Sometimes used by newer API-enabled models
        '/print.php',       // Sometimes seen on web-enabled models
        '/cgi-bin/epos/service.cgi' // Some Epson thermal printers use this
      ];
      
      // Try various content types too - some printers are picky
      const contentTypes = [
        'application/x-www-form-urlencoded',
        'application/octet-stream',
        'text/plain',
        'application/zpl'
      ];
      
      // Use no-cors mode to bypass CORS issues
      // Note: This makes the response "opaque" so we can't actually check status,
      // but we'll try anyway and just assume success if no error is thrown
      
      // Try each endpoint with different content types
      for (const endpoint of endpoints) {
        for (const contentType of contentTypes) {
          try {
            let body;
            if (contentType === 'application/x-www-form-urlencoded') {
              body = `data=${encodeURIComponent(zplData)}`;
            } else {
              body = zplData;
            }
            
            if (process.env.NODE_ENV === 'development') {
              console.log(`Trying direct print to ${printer.address}${endpoint} with ${contentType}...`);
            }
            
            const fetchOptions = {
              method: 'POST',
              headers: {
                'Content-Type': contentType,
              },
              body: body,
              signal: controller.signal,
              mode: 'no-cors' as RequestMode
            };
            
            await fetch(`http://${printer.address}${endpoint}`, fetchOptions);
            
            // If we get here, fetch succeeded (though we can't actually verify the status with no-cors)
            clearTimeout(timeoutId);
            
            // Log successful direct print
            await this.logPrintJob(printer.id, 'direct', 'completed', zplData.length);
            return { 
              success: true, 
              message: `Print job may have been sent directly to printer via ${endpoint} (could not verify due to CORS)` 
            };
          } catch (endpointError) {
            if (process.env.NODE_ENV === 'development') {
              console.log(`Failed to connect to ${endpoint} with ${contentType}:`, endpointError);
            }
            // Continue to next endpoint/content type
          }
        }
      }
      
      // All endpoints failed
      clearTimeout(timeoutId);
      if (process.env.NODE_ENV === 'development') {
        console.log('Direct printer access failed, using fallback.');
      }
      return { 
        success: false, 
        message: 'Direct printing failed, falling back to alternative method' 
      };
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.log('Direct printer access failed, using fallback:', error);
      }
      return { 
        success: false, 
        message: 'Direct printing failed, falling back to alternative method' 
      };
    }
  }

  /**
   * Handle fallback methods for Zebra printing when direct printing fails
   */
  private async handleZebraPrintFallback(
    printer: Printer,
    zplData: string
  ): Promise<{ success: boolean; message: string }> {
    try {
      // Generate a unique job ID
      const jobId = `print_${Date.now()}_${Math.random().toString(36).substring(2, 10)}`;
      
      // First try to store in Firestore
      let firestoreSuccess = false;
      try {
        // Log the print job to Firestore
        await this.logPrintJob(printer.id, 'qrcode', 'pending', zplData.length);
        
        // Store the ZPL data in Firestore
        await this.storePrintData(jobId, zplData);
        firestoreSuccess = true;
      } catch (firestoreError) {
        if (process.env.NODE_ENV === 'development') {
          console.warn('Failed to store print job in Firestore, using localStorage instead:', firestoreError);
        }
        // Fall back to localStorage if Firestore fails
        this.storePrintDataInLocalStorage(jobId, 'zpl', printer.id);
      }
      
      // Generate a temporary unique URL for this print job
      const printUrl = this.generatePrintUrl(jobId, printer.id, !firestoreSuccess);
      
      // Generate QR code for the print URL
      const qrCodeDataUrl = await QRCode.toDataURL(printUrl);
      
      // Show the QR code to the user
      this.showPrintQrCode(qrCodeDataUrl, 'zpl');
      
      return {
        success: true,
        message: 'Scan the QR code with a device on the same network as the printer'
      };
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('QR code generation failed:', error);
      }
      
      // Try using the cloud function as a last resort
      return await this.sendToNetworkPrinter(printer, zplData);
    }
  }

  /**
   * Generate a URL that can be used to trigger printing when accessed
   */
  private generatePrintUrl(jobId: string, printerId: string, useLocalStorage: boolean = false): string {
    // Base URL for the print proxy
    const baseUrl = window.location.origin;
    
    // For localStorage-based printing, we'll include more parameters directly in the URL
    if (useLocalStorage) {
      // Add printer address and job ID to URL
      return `${baseUrl}/print-proxy?job=${jobId}&printer=${printerId}&local=true`;
    }
    
    // Standard Firestore-based URL
    return `${baseUrl}/print-proxy?job=${jobId}&printer=${printerId}`;
  }

  /**
   * Store print data in Firestore for later retrieval
   */
  private async storePrintData(jobId: string, printData: string): Promise<void> {
    await addDoc(collection(db, 'printData'), {
      jobId,
      data: printData,
      createdAt: serverTimestamp(),
      expiresAt: new Date(Date.now() + 24 * 60 * 60 * 1000) // 24 hour expiration
    });
  }

  /**
   * Store print data in local storage when cloud functions are not available
   */
  private storePrintDataInLocalStorage(
    printData: string, 
    format: 'zpl' | 'esc-pos' | 'pdf',
    printerId: string
  ): { success: boolean; message: string } {
    try {
      // Generate a unique ID for this print job
      const jobId = `printjob_${Date.now()}_${Math.floor(Math.random() * 10000)}`;
      
      // Create a print job object
      const printJob = {
        id: jobId,
        printerId,
        data: printData,
        format,
        status: 'pending',
        createdAt: new Date().toISOString()
      };
      
      // Store in local storage
      localStorage.setItem(`printjob_${jobId}`, JSON.stringify(printJob));
      
      if (process.env.NODE_ENV === 'development') {
        console.log(`Print job ${jobId} stored in local storage, format: ${format}`);
      }
      
      return {
        success: true,
        message: 'Print job stored locally. Please use the Print Agent to retrieve it.'
      };
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Error storing print job in local storage:', error);
      }
      return {
        success: false,
        message: `Failed to store print job: ${error instanceof Error ? error.message : String(error)}`
      };
    }
  }

  /**
   * Show QR code with print data for manual scanning
   */
  private showPrintQrCode(
    printData: string, 
    format: 'zpl' | 'esc-pos' | 'pdf'
  ): { success: boolean; message: string } {
    try {
      // Generate a data URL for the QR code
      const printUrl = this.generatePrintUrl(printData, format);
      
      // Generate QR code
      QRCode.toDataURL(printUrl, { width: 300, margin: 2 })
        .then((qrCodeDataUrl) => {
          this.showQrCodeModal(qrCodeDataUrl, format);
        })
        .catch((error) => {
          if (process.env.NODE_ENV === 'development') {
            console.error('Error generating QR code:', error);
          }
        });
      
      return {
        success: true,
        message: 'QR code displayed for manual printing'
      };
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Error showing QR code:', error);
      }
      return {
        success: false,
        message: `Failed to show QR code: ${error instanceof Error ? error.message : String(error)}`
      };
    }
  }

  /**
   * Log print job to Firestore for tracking
   */
  private async logPrintJob(
    printerId: string, 
    method: 'direct' | 'qrcode' | 'cloud',
    status: 'pending' | 'completed' | 'failed',
    contentLength: number
  ): Promise<string> {
    const docRef = await addDoc(collection(db, 'printJobs'), {
      printerId,
      method,
      status,
      contentLength,
      createdAt: serverTimestamp()
    });
    
    return docRef.id;
  }

  /**
   * Send data to a network printer via cloud function
   * This is a fallback method when all else fails
   */
  private async sendToNetworkPrinter(
    printer: Printer,
    printData: string
  ): Promise<{ success: boolean; message: string }> {
    try {
      // Send print data to our Firebase Cloud Function
      const cloudFunctionUrl = getEnv('REACT_APP_PRINT_FUNCTION_URL') || 
                              'https://us-central1-fllboats.cloudfunctions.net/sendPrintJob';
      
      const response = await fetch(cloudFunctionUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          printerId: printer.id,
          printerAddress: printer.address,
          printerPort: printer.port || 9100,
          printData,
        }),
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Server returned ${response.status}: ${errorText}`);
      }
      
      const result = await response.json();
      return {
        success: true,
        message: result.message || 'Print job sent to printer successfully',
      };
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Network printing error:', error);
      }
      return {
        success: false,
        message: `Failed to send to network printer: ${error instanceof Error ? error.message : 'Unknown error'}`,
      };
    }
  }

  /**
   * Generate ZPL code for Zebra printers
   */
  private generateZPL(
    printer: Printer,
    template: LabelTemplate,
    products: Product[],
    copies: number
  ): string {
    // Convert inches to dots based on printer DPI
    const dpi = printer.dpi || 203; // Default to 203 DPI if not specified
    const widthDots = Math.round(template.width * dpi);
    const heightDots = Math.round(template.height * dpi);
    
    const zplCommands: string[] = [];
    
    // Process each product to generate a label
    products.forEach(product => {
      // Initialize ZPL with label format
      zplCommands.push(
        '^XA', // Start of ZPL
        `^PW${widthDots}`, // Label width
        `^LL${heightDots}`, // Label length
        '^CI28', // Unicode encoding
        '^LH0,0', // Label home position
      );

      // Process each element on the template
      template.elements.forEach(element => {
        // Convert element position and size to dots
        const x = Math.round(element.x * (dpi / 96)); // Convert from CSS pixels to dots
        const y = Math.round(element.y * (dpi / 96));
        const width = Math.round(element.width * (dpi / 96));
        const height = Math.round(element.height * (dpi / 96));
        
        // Handle different element types
        switch (element.type) {
          case 'text':
            const text = this.getElementContent(element, product);
            const fontSize = Math.round((element.fontSize || 12) * (dpi / 72)); // Convert pt to dots
            
            zplCommands.push(
              `^FO${x},${y}`, // Field origin
              `^A0,${fontSize}`, // Font
              element.fontWeight === 'bold' ? '^SB1' : '', // Bold
              element.fontStyle === 'italic' ? '^SI1' : '', // Italic
              `^FB${width},1,0,${this.getZPLAlignment(element.textAlign || 'left')}`, // Text block
              `^FD${text}^FS` // Field data and end field
            );
            break;
            
          case 'barcode':
            const barcodeData = this.getElementContent(element, product) || '0';
            zplCommands.push(
              `^FO${x},${y}`, // Field origin
              '^BC', // Code 128 barcode
              `^BY2`, // Module width
              `^BXN,3,200`, // Code settings
              `^FD${barcodeData}^FS` // Barcode data and end field
            );
            break;
            
          case 'qrcode':
            const qrData = this.getElementContent(element, product) || '0';
            zplCommands.push(
              `^FO${x},${y}`, // Field origin
              '^BQN,2,6', // QR code
              `^FDMA,${qrData}^FS` // QR data and end field
            );
            break;
            
          case 'rectangle':
            zplCommands.push(
              `^FO${x},${y}`, // Field origin
              `^GB${width},${height},${element.borderWidth || 1}^FS` // Graphic box
            );
            break;
            
          case 'line':
            zplCommands.push(
              `^FO${x},${y}`, // Field origin
              `^GB${width},1,${element.borderWidth || 1}^FS` // Horizontal line
            );
            break;
            
          // Other element types would be handled here
        }
      });
      
      // End ZPL and set number of copies
      zplCommands.push(
        `^PQ${copies}`, // Print quantity
        '^XZ' // End of ZPL
      );
    });
    
    return zplCommands.join('\n');
  }

  /**
   * Generate a PDF blob for standard printers
   */
  private async generatePDF(
    printer: Printer,
    template: LabelTemplate,
    products: Product[],
    copies: number
  ): Promise<Blob> {
    // Create a new PDF with appropriate measurements
    // Convert inches to points (72 points per inch)
    const widthPt = template.width * 72;
    const heightPt = template.height * 72;
    
    // Create the PDF document
    const doc = new jsPDF({
      orientation: widthPt > heightPt ? 'landscape' : 'portrait',
      unit: 'pt',
      format: [widthPt, heightPt]
    });
    
    // Process each product
    products.forEach((product, productIndex) => {
      // Add a new page for each product after the first one
      if (productIndex > 0) {
        doc.addPage([widthPt, heightPt]);
      }
      
      // Render each element
      template.elements.forEach(element => {
        this.drawElementOnPDF(doc, element, product);
      });
      
      // Add product copies if needed
      for (let i = 1; i < copies; i++) {
        doc.addPage([widthPt, heightPt]);
        template.elements.forEach(element => {
          this.drawElementOnPDF(doc, element, product);
        });
      }
    });
    
    // Return the PDF as a blob
    return doc.output('blob');
  }

  /**
   * Draw an element on the PDF
   */
  private drawElementOnPDF(
    doc: jsPDF,
    element: LabelElement,
    product: Product
  ): void {
    // Get element position in points (72 points per inch)
    const x = element.x * (72 / 96); // Convert from CSS pixels to points
    const y = element.y * (72 / 96);
    const width = element.width * (72 / 96);
    const height = element.height * (72 / 96);
    
    // Handle different element types
    switch (element.type) {
      case 'text':
        const text = this.getElementContent(element, product);
        const fontSize = element.fontSize || 12;
        
        // Set text styles
        doc.setFont(element.fontFamily || 'helvetica', element.fontStyle || 'normal');
        doc.setFontSize(fontSize);
        doc.setTextColor(element.color || '#000000');
        
        // Handle text alignment
        let textX = x;
        const textAlign = element.textAlign || 'left';
        if (textAlign === 'center') {
          textX = x + width / 2;
        } else if (textAlign === 'right') {
          textX = x + width;
        }
        
        // Draw text with alignment
        doc.text(text, textX, y + fontSize * 0.75, { 
          align: textAlign as "left" | "center" | "right" | "justify"
        });
        break;
        
      case 'rectangle':
        doc.setDrawColor(element.borderColor || '#000000');
        doc.setLineWidth(element.borderWidth || 1);
        
        if (element.backgroundColor) {
          doc.setFillColor(element.backgroundColor);
          doc.rect(x, y, width, height, 'FD'); // Fill and stroke
        } else {
          doc.rect(x, y, width, height, 'S'); // Stroke only
        }
        break;
        
      case 'line':
        doc.setDrawColor(element.color || '#000000');
        doc.setLineWidth(element.borderWidth || 1);
        doc.line(x, y, x + width, y);
        break;
        
      case 'barcode':
      case 'qrcode':
        // For a complete implementation, we would use a barcode library
        // For now, just add a placeholder rectangle with text
        const barcodeText = this.getElementContent(element, product);
        
        doc.setDrawColor('#000000');
        doc.setLineWidth(1);
        doc.rect(x, y, width, height, 'S');
        
        // Add barcode text
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(10);
        doc.setTextColor('#000000');
        
        // Center the text below the barcode/QR code
        doc.text(barcodeText, x + width / 2, y + height - 10, { align: 'center' });
        break;
    }
  }

  /**
   * Check if a printer is a Zebra printer based on its configuration
   */
  private isZebraPrinter(printer: Printer): boolean {
    // Check if the printer is explicitly marked as Zebra
    if (printer.manufacturer?.toLowerCase() === 'zebra') {
      return true;
    }
    
    // Check common Zebra model patterns in the name or model
    const zebraPatterns = ['zebra', 'zd', 'zt', 'gk', 'gx', 'zp', 'tlp'];
    const nameLower = printer.name.toLowerCase();
    const modelLower = (printer.model || '').toLowerCase();
    
    return zebraPatterns.some(pattern => 
      nameLower.includes(pattern) || modelLower.includes(pattern)
    );
  }

  /**
   * Get the content for an element, replacing data source placeholders with product data
   */
  private getElementContent(element: LabelElement, product: Product): string {
    if (!element.dataSource) return element.content || '';
    
    // Extract data from product based on dataSource
    switch (element.dataSource) {
      case 'product.name':
        return product.name || '';
      case 'product.price':
        return formatCurrency(product.price);
      case 'product.sku':
        return product.sku || '';
      case 'product.category':
        return product.category || '';
      case 'product.location':
        return product.locationIds ? product.locationIds.join(', ') : '';
      case 'product.id':
        return product.id || '';
      case 'product.upc':
        return product.sku || ''; // Use SKU as UPC since UPC isn't available
      default:
        return element.content || '';
    }
  }

  /**
   * Convert text alignment to ZPL alignment
   */
  private getZPLAlignment(alignment: string): string {
    switch (alignment) {
      case 'left': return 'L';
      case 'center': return 'C';
      case 'right': return 'R';
      default: return 'L';
    }
  }
  
  /**
   * Test a printer by sending a simple test page
   */
  async testPrinter(printer: Printer): Promise<boolean> {
    try {
      // Create a simple test label
      const testTemplate: LabelTemplate = {
        id: 'test_template',
        name: 'Test Template',
        type: 'custom',
        width: 4,
        height: 3,
        elements: [
          {
            id: 'test_title',
            type: 'text',
            x: 10,
            y: 10,
            width: 380,
            height: 30,
            content: `Test Print - ${printer.name}`,
            fontSize: 16,
            fontWeight: 'bold',
            fontFamily: 'Arial',
            textAlign: 'center'
          },
          {
            id: 'test_date',
            type: 'text',
            x: 10,
            y: 50,
            width: 380,
            height: 20,
            content: `Date: ${new Date().toLocaleString()}`,
            fontSize: 12,
            fontFamily: 'Arial',
            textAlign: 'center'
          },
          {
            id: 'test_rect',
            type: 'rectangle',
            x: 10,
            y: 80,
            width: 380,
            height: 180,
            borderWidth: 1
          }
        ]
      };
      
      // Create a test product
      const testProduct: Product = {
        id: 'test_product',
        name: 'Test Product',
        price: 0,
        sku: 'TEST-123',
        category: 'Test',
        itemType: 'retail',
        active: true,
        createdAt: new Date(),
        updatedAt: new Date()
      };
      
      // Send test print
      const result = await this.printLabel(printer, testTemplate, [testProduct], 1);
      return result.success;
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Test print error:', error);
      }
      return false;
    }
  }

  /**
   * Check if a local print agent is available
   * Only attempts to connect directly when in development mode
   */
  async checkLocalPrintAgent(hostname: string = 'localhost', port: number = 8080): Promise<boolean> {
    try {
      // Skip local connection attempts in production environments
      // Only try to connect directly to localhost when in development
      const isDev = window.location.hostname === 'localhost' || 
                   window.location.hostname === '127.0.0.1';
      
      if (!isDev) {
        if (process.env.NODE_ENV === 'development') {
          console.log('Running in production environment, checking cloud-based Print Agents...');
        }
        // In production, check for available agents through cloud functions
        const agents = await this.getAvailablePrintAgents();
        const hasOnlineAgents = agents.some(agent => agent.status === 'ONLINE');
        
        if (hasOnlineAgents) {
          if (process.env.NODE_ENV === 'development') {
            console.log('Found online Print Agents in production environment');
          }
          return true;
        }
        if (process.env.NODE_ENV === 'development') {
          console.log('No online Print Agents found in production environment');
        }
        return false;
      }
      
      if (process.env.NODE_ENV === 'development') {
        console.log(`Checking for Print Agent at ${hostname}:${port}...`);
      }
      
      // Try to connect to the local print agent's health endpoint
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 3000); // 3 second timeout
      
      try {
        // First attempt with regular CORS
        const response = await fetch(`http://${hostname}:${port}/health`, {
          signal: controller.signal
        });
        
        // Clear the timeout
        clearTimeout(timeoutId);
        
        if (response.ok) {
          const data = await response.json();
          if (process.env.NODE_ENV === 'development') {
            console.log('Print Agent found:', data);
          }
          
          // Store available printers from the agent
          if (data.printers) {
            localStorage.setItem('print_agent_printers', JSON.stringify(data.printers));
            if (process.env.NODE_ENV === 'development') {
              console.log(`Print Agent has ${data.printers.length} configured printers`);
            }
          }
          
          return true;
        } else {
          if (process.env.NODE_ENV === 'development') {
            console.log(`Print Agent health check failed: HTTP ${response.status} - ${response.statusText}`);
          }
        }
      } catch (fetchError) {
        // Clear the timeout
        clearTimeout(timeoutId);
        
        if (fetchError.name === 'AbortError') {
          if (process.env.NODE_ENV === 'development') {
            console.log('Print Agent health check timed out');
          }
        } else {
          if (process.env.NODE_ENV === 'development') {
            console.log('Print Agent CORS request failed, trying no-cors mode as fallback');
          }
          
          // Try with no-cors as fallback (only in development)
          try {
            // Note: This will return an opaque response we can't read,
            // but if it doesn't throw an error, the server might be there
            const noCorsController = new AbortController();
            const noCorsTimeoutId = setTimeout(() => noCorsController.abort(), 3000);
            
            await fetch(`http://${hostname}:${port}/health`, {
              mode: 'no-cors',
              signal: noCorsController.signal
            });
            
            clearTimeout(noCorsTimeoutId);
            
            // If we get here without an error, the agent might be available
            // but with CORS misconfiguration
            if (process.env.NODE_ENV === 'development') {
              console.log('Print Agent seems available (no-cors mode), but CORS headers may be misconfigured');
            }
            
            // We'll consider it potentially available, let the actual print request try
            return true;
          } catch (noCorsError) {
            if (process.env.NODE_ENV === 'development') {
              console.log('Print Agent not available via no-cors mode either:', 
                noCorsError instanceof Error ? noCorsError.message : String(noCorsError));
            }
            return false;
          }
        }
        return false;
      }
      
      // If we get here, the first attempt failed but didn't throw
      return false;
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.log('Print Agent connection error:', error instanceof Error ? error.message : String(error));
      }
      return false;
    }
  }
  
  /**
   * Get the saved print agent configuration
   */
  getPrintAgentConfig(): { enabled: boolean; hostname: string; port: number; authKey?: string } {
    try {
      const configStr = localStorage.getItem('print_agent_config');
      if (configStr) {
        return JSON.parse(configStr);
      }
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Error reading print agent config:', error);
      }
    }
    
    // Default config
    return {
      enabled: false,
      hostname: 'localhost',
      port: 8080
    };
  }
  
  /**
   * Save print agent configuration
   */
  savePrintAgentConfig(config: { enabled: boolean; hostname: string; port: number; authKey?: string }): void {
    try {
      localStorage.setItem('print_agent_config', JSON.stringify(config));
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Error saving print agent config:', error);
      }
    }
  }
  
  /**
   * Print via local print agent
   */
  private async printViaLocalAgent(
    printer: Printer,
    printData: string,
    format: 'zpl' | 'esc-pos' | 'pdf' = 'zpl'
  ): Promise<{ success: boolean; message: string }> {
    try {
      const agentConfig = this.getPrintAgentConfig();
      
      if (!agentConfig.enabled || !agentConfig.authKey) {
        throw new Error('Print agent is not enabled or not configured');
      }
      
      if (process.env.NODE_ENV === 'development') {
        console.log(`Sending print job to local Print Agent at ${agentConfig.hostname}:${agentConfig.port}...`);
      }
      
      // First try with standard CORS
      try {
        // Add timeout to prevent hanging
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 8000); // 8 second timeout
        
        // Send print job to local agent
        const response = await fetch(`http://${agentConfig.hostname}:${agentConfig.port}/print`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            authKey: agentConfig.authKey,
            printerId: printer.id,
            printData,
            format
          }),
          signal: controller.signal
        });
        
        // Clear timeout
        clearTimeout(timeoutId);
        
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(`Print agent error: ${errorData.message || response.statusText}`);
        }
        
        const result = await response.json();
        return {
          success: true,
          message: result.message || 'Print job sent via local agent',
        };
      } catch (corsError) {
        // If this is a CORS error, try with no-cors mode
        if (corsError instanceof TypeError || 
            (corsError instanceof Error && corsError.message.includes('Failed to fetch'))) {
          
          if (process.env.NODE_ENV === 'development') {
            console.log('CORS issue when connecting to Print Agent, trying no-cors mode...');
          }
          
          try {
            // Try with no-cors mode (we won't get a readable response)
            await fetch(`http://${agentConfig.hostname}:${agentConfig.port}/print`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                authKey: agentConfig.authKey,
                printerId: printer.id,
                printData,
                format
              }),
              mode: 'no-cors'
            });
            
            // If we get here, the request didn't throw an error
            // We can't verify success due to opaque response, but assume it worked
            if (process.env.NODE_ENV === 'development') {
              console.log('Print job sent via local agent (no-cors mode)');
            }
            
            return {
              success: true,
              message: 'Print job may have been sent via local agent (CORS limitations prevent confirmation)',
            };
          } catch (noCorsError) {
            if (process.env.NODE_ENV === 'development') {
              console.error('No-cors print attempt failed:', noCorsError);
            }
            throw new Error('Failed to connect to local Print Agent: CORS and no-cors attempts failed');
          }
        } else {
          // Re-throw original error if it's not a CORS/fetch issue
          throw corsError;
        }
      }
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Local agent printing error:', error);
      }
      
      // Try the cloud function route instead
      try {
        if (process.env.NODE_ENV === 'development') {
          console.log('Falling back to Firebase Cloud Functions...');
        }
        
        // Get available print agents from cloud
        const printAgents = await this.getAvailablePrintAgents();
        
        // Find a matching agent for this printer
        const matchingAgent = printAgents.find(agent => 
          agent.printers?.some(p => p.id === printer.id)
        );
        
        if (matchingAgent) {
          if (process.env.NODE_ENV === 'development') {
            console.log('Found matching Print Agent in cloud, sending job via Firebase...');
          }
          const result = await this.queuePrintJob(matchingAgent.id, printer.id, printData, format);
          
          if (result) {
            return {
              success: true,
              message: 'Print job queued via Cloud Functions'
            };
          }
        }
      } catch (cloudError) {
        if (process.env.NODE_ENV === 'development') {
          console.error('Cloud fallback failed:', cloudError);
        }
      }
      
      return {
        success: false,
        message: `Failed to print via local agent: ${error instanceof Error ? error.message : 'Unknown error'}`,
      };
    }
  }

  /**
   * Convert a Blob to base64 string
   */
  private blobToBase64(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        if (typeof reader.result === 'string') {
          // Remove the data URL prefix
          const base64data = reader.result.split(',')[1];
          resolve(base64data);
        } else {
          reject(new Error('Failed to convert blob to base64'));
        }
      };
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  }

  /**
   * Synchronize printers from the print agent to Firestore
   */
  async syncPrintersFromAgent(): Promise<{success: boolean; message: string; printers?: Printer[]}> {
    try {
      // Check if print agent is available
      const agentConfig = this.getPrintAgentConfig();
      const isAgentAvailable = await this.checkLocalPrintAgent(
        agentConfig.hostname, 
        agentConfig.port
      );
      
      if (!isAgentAvailable) {
        return {
          success: false,
          message: 'Print Agent is not available or not responding'
        };
      }
      
      // Get printers from storage
      const agentPrintersRaw = localStorage.getItem('print_agent_printers');
      if (!agentPrintersRaw) {
        return {
          success: false,
          message: 'No printers found in Print Agent'
        };
      }
      
      const agentPrinters = JSON.parse(agentPrintersRaw);
      const syncedPrinters: Printer[] = [];
      
      // Create Firestore printer objects from agent printers
      for (const agentPrinter of agentPrinters) {
        // Create a new printer object
        const newPrinter: Printer = {
          id: `printer_${Date.now()}_${Math.floor(Math.random() * 1000)}`,
          name: agentPrinter.name || 'Unknown Printer',
          type: agentPrinter.type || 'network',
          address: agentPrinter.address,
          port: agentPrinter.port || 9100,
          manufacturer: agentPrinter.manufacturer,
          model: agentPrinter.model,
          dpi: agentPrinter.dpi || 203,
          status: 'online',
          connected: true
        };
        
        // Add to Firestore
        await setDoc(doc(db, 'labelPrinters', newPrinter.id), {
          ...newPrinter,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        });
        
        syncedPrinters.push(newPrinter);
      }
      
      return {
        success: true,
        message: `Successfully synchronized ${syncedPrinters.length} printer(s)`,
        printers: syncedPrinters
      };
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Failed to sync printers from agent:', error);
      }
      return {
        success: false,
        message: `Error synchronizing printers: ${error instanceof Error ? error.message : 'Unknown error'}`
      };
    }
  }

  /**
   * Show QR code modal dialog
   */
  private showQrCodeModal(qrCodeDataUrl: string, format: 'zpl' | 'esc-pos' | 'pdf'): void {
    // Create a modal dialog to show the QR code
    const modal = document.createElement('div');
    modal.style.position = 'fixed';
    modal.style.top = '0';
    modal.style.left = '0';
    modal.style.right = '0';
    modal.style.bottom = '0';
    modal.style.backgroundColor = 'rgba(0, 0, 0, 0.5)';
    modal.style.display = 'flex';
    modal.style.flexDirection = 'column';
    modal.style.alignItems = 'center';
    modal.style.justifyContent = 'center';
    modal.style.zIndex = '10000';
    
    const container = document.createElement('div');
    container.style.backgroundColor = '#fff';
    container.style.padding = '20px';
    container.style.borderRadius = '5px';
    container.style.maxWidth = '400px';
    container.style.width = '90%';
    container.style.textAlign = 'center';
    
    const title = document.createElement('h3');
    title.textContent = `Print ${format.toUpperCase()} Data`;
    title.style.marginBottom = '15px';
    
    const instructions = document.createElement('p');
    instructions.textContent = 'Scan this QR code with the Print Agent app to print.';
    instructions.style.marginBottom = '15px';
    
    const qrImg = document.createElement('img');
    qrImg.src = qrCodeDataUrl;
    qrImg.style.width = '200px';
    qrImg.style.height = '200px';
    qrImg.style.margin = '10px auto';
    qrImg.style.display = 'block';
    
    const note = document.createElement('p');
    note.textContent = 'The print job will be available for 24 hours.';
    note.style.fontSize = '12px';
    note.style.color = '#666';
    note.style.marginTop = '15px';
    
    const closeButton = document.createElement('button');
    closeButton.textContent = 'Close';
    closeButton.style.marginTop = '15px';
    closeButton.style.padding = '8px 16px';
    closeButton.style.backgroundColor = '#4285f4';
    closeButton.style.color = '#fff';
    closeButton.style.border = 'none';
    closeButton.style.borderRadius = '4px';
    closeButton.style.cursor = 'pointer';
    closeButton.onclick = () => {
      document.body.removeChild(modal);
    };
    
    container.appendChild(title);
    container.appendChild(instructions);
    container.appendChild(qrImg);
    container.appendChild(note);
    container.appendChild(closeButton);
    modal.appendChild(container);
    
    document.body.appendChild(modal);
  }

  /**
   * Generate a URL with embedded print data
   */
  private generatePrintDataUrl(printData: string, format: 'zpl' | 'esc-pos' | 'pdf'): string {
    // Compress the data to make the URL shorter
    const compressedData = printData; // In a real implementation, compress this
    
    // Generate a data URL
    const baseUrl = `https://printdata.example.com/?data=${encodeURIComponent(compressedData)}&format=${format}`;
    
    return baseUrl;
  }
}

/**
 * Format a price as currency
 */
function formatCurrency(price: any): string {
  // Handle undefined, null, or non-numeric values
  if (price === undefined || price === null || isNaN(parseFloat(price))) {
    return 'N/A';
  }
  
  // Convert to number if it's a string
  const numericPrice = typeof price === 'string' ? parseFloat(price) : price;
  
  // Use Number.isFinite to ensure it's a valid finite number
  if (!Number.isFinite(numericPrice)) {
    return 'N/A';
  }
  
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(numericPrice);
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.error('Error formatting currency:', error);
    }
    return `$${numericPrice.toFixed(2)}`;
  }
}

// Create and export singleton instance
export const printService = new PrintService(); 